import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

// Per-search Places session token so Google bills the type-ahead keystrokes +
// the one details lookup as a single (cheaper) session. Reset after each pick.
function newSessionToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Live address suggestions. Primary provider is Google Places Autocomplete (New)
// via our backend proxy (API key stays server-side); the City of Edmonton civic
// geocoder + OpenRouteService remain as automatic fallback. Generic enough to be
// reused anywhere an address needs to autofill lat/lng (customer register,
// driver form, admin customer form) via optional label/id/testIdPrefix overrides.
export function AddressAutocomplete({ value, onChange, onSelect, label = "Address", id = "address-autocomplete", testIdPrefix = "customer-register-address" }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const sessionToken = useRef(newSessionToken());

  useEffect(() => setQuery(value || ""), [value]);

  const search = (text) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!text || text.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/customer/geocode/autocomplete`, {
          params: { text, session_token: sessionToken.current },
        });
        setResults(data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  };

  // Google predictions carry only a place_id — resolve to coords on selection
  // (closing the billing session). Fallback (civic/ORS) rows already have coords.
  const choose = async (r) => {
    setOpen(false);
    if (r.place_id && !r.latitude) {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE}/customer/geocode/details`, {
          params: { place_id: r.place_id, session_token: sessionToken.current },
        });
        setQuery(data.label || data.address || "");
        onSelect?.(data);
      } catch {
        setQuery(r.label || "");
        onSelect?.(r);
      } finally {
        setLoading(false);
      }
    } else {
      setQuery(r.address || r.label || "");
      onSelect?.(r);
    }
    setResults([]);
    sessionToken.current = newSessionToken();
  };

  return (
    <div className="relative space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          data-testid={`${testIdPrefix}-input`}
          value={query}
          placeholder="Start typing an address..."
          onChange={(e) => {
            setQuery(e.target.value);
            onChange?.(e.target.value);
            search(e.target.value);
          }}
          onFocus={() => results.length > 0 && setOpen(true)}
          autoComplete="off"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-text-muted" />}
      </div>
      {open && results.length > 0 && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border border-border bg-surface shadow-lg"
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {results.map((r, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => choose(r)}
              className="flex w-full items-start gap-2 border-b border-border/60 px-3 py-2 text-left text-sm text-text-main last:border-0 hover:bg-background"
              data-testid={`${testIdPrefix}-suggestion-${idx}`}
            >
              <MapPin size={14} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-text-muted" />
              <span>{r.label || r.address}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
